import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface PhoneData {
  homePhone?: string;
  mobilePhone?: string;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class PhoneStateService {
  private readonly STORAGE_KEY = 'sicred_last_phones';
  private readonly MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 horas

  private lastPhonesSubject = new BehaviorSubject<PhoneData | null>(this.loadFromStorage());
  public lastPhones$: Observable<PhoneData | null> = this.lastPhonesSubject.asObservable();

  constructor() {
    // Limpar dados antigos ao inicializar
    this.cleanOldData();
  }

  /**
   * Salva os telefones no estado e no localStorage
   */
  savePhones(homePhone?: string, mobilePhone?: string): void {
    const phoneData: PhoneData = {
      homePhone: this.cleanPhone(homePhone),
      mobilePhone: this.cleanPhone(mobilePhone),
      timestamp: Date.now()
    };

    // Só salvar se houver pelo menos um telefone
    if (phoneData.homePhone || phoneData.mobilePhone) {
      this.lastPhonesSubject.next(phoneData);
      this.saveToStorage(phoneData);
    }
  }

  /**
   * Retorna os últimos telefones salvos
   */
  getLastPhones(): PhoneData | null {
    const data = this.lastPhonesSubject.value;
    
    // Verificar se os dados não expiraram
    if (data && this.isDataValid(data)) {
      return data;
    }
    
    // Se expirou, limpar
    this.clearPhones();
    return null;
  }

  /**
   * Retorna apenas o último telefone fixo
   */
  getLastHomePhone(): string | null {
    const data = this.getLastPhones();
    return data?.homePhone || null;
  }

  /**
   * Retorna apenas o último celular
   */
  getLastMobilePhone(): string | null {
    const data = this.getLastPhones();
    return data?.mobilePhone || null;
  }

  /**
   * Verifica se há telefone fixo salvo
   */
  hasHomePhone(): boolean {
    const data = this.getLastPhones();
    return !!(data?.homePhone);
  }

  /**
   * Verifica se há celular salvo
   */
  hasMobilePhone(): boolean {
    const data = this.getLastPhones();
    return !!(data?.mobilePhone);
  }

  /**
   * Limpa os telefones salvos
   */
  clearPhones(): void {
    this.lastPhonesSubject.next(null);
    this.removeFromStorage();
  }

  /**
   * Formata o telefone para exibição (com máscara)
   */
  formatHomePhone(phone: string): string {
    const cleaned = this.cleanPhone(phone);
    if (!cleaned) return '';
    
    // Formato: (99) 9999-9999
    if (cleaned.length === 10) {
      return `(${cleaned.substring(0, 2)}) ${cleaned.substring(2, 6)}-${cleaned.substring(6)}`;
    }
    return phone;
  }

  /**
   * Formata o celular para exibição (com máscara)
   */
  formatMobilePhone(phone: string): string {
    const cleaned = this.cleanPhone(phone);
    if (!cleaned) return '';
    
    // Formato: (99) 99999-9999
    if (cleaned.length === 11) {
      return `(${cleaned.substring(0, 2)}) ${cleaned.substring(2, 7)}-${cleaned.substring(7)}`;
    }
    return phone;
  }

  // ============= MÉTODOS PRIVADOS =============

  /**
   * Remove caracteres não numéricos do telefone
   */
  private cleanPhone(phone?: string): string {
    if (!phone) return '';
    return phone.replace(/\D/g, '');
  }

  /**
   * Verifica se os dados ainda são válidos (não expiraram)
   */
  private isDataValid(data: PhoneData): boolean {
    const age = Date.now() - data.timestamp;
    return age < this.MAX_AGE_MS;
  }

  /**
   * Salva os dados no localStorage
   */
  private saveToStorage(data: PhoneData): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Erro ao salvar telefones no localStorage:', error);
    }
  }

  /**
   * Carrega os dados do localStorage
   */
  private loadFromStorage(): PhoneData | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const data: PhoneData = JSON.parse(stored);
        
        // Verificar se não expirou
        if (this.isDataValid(data)) {
          return data;
        }
      }
    } catch (error) {
      console.error('Erro ao carregar telefones do localStorage:', error);
    }
    return null;
  }

  /**
   * Remove os dados do localStorage
   */
  private removeFromStorage(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Erro ao remover telefones do localStorage:', error);
    }
  }

  /**
   * Limpa dados antigos ao inicializar o serviço
   */
  private cleanOldData(): void {
    const data = this.loadFromStorage();
    if (data && !this.isDataValid(data)) {
      this.removeFromStorage();
      this.lastPhonesSubject.next(null);
    }
  }
}
